import { useMemo } from "react";
import { Table } from "react-bootstrap";
import {
  horasPorCuatrimestre,
  labelCursado,
} from "components/EspaciosTable/EspaciosTable";

export interface ComparadorEspacioBase {
  id: number | string;
  nombre: string;
  anio?: number | string;
  horasSemanales?: number | string;
  cuatrimestre?: number | null;
  area?: string;
  tipoEspacio?: string;
}

export interface ComparadorEspacioPci {
  id?: number | string;
  espacioCurricularId?: number | string; // Vincula con el base
  nombre: string;
  anio?: number | string;
  horasSemanales?: number | string;
  cuatrimestre?: number | null;
  area?: string;
  tipoEspacio?: string;
}

type EstadoComparacion = "sin_cambio" | "modificado" | "agregado" | "eliminado";

interface FilaComparacion {
  nombre: string;
  anio: number | string;
  horasBase?: number | string;
  horasPci?: number | string;
  cuatrimestreBase?: number | null;
  cuatrimestrePci?: number | null;
  area?: string;
  tipoEspacio?: string;
  estado: EstadoComparacion;
}

interface Props {
  espaciosBase: ComparadorEspacioBase[];
  espaciosPci: ComparadorEspacioPci[];
  maxHeight?: string;
}

const ESTADO_COLORS: Record<EstadoComparacion, string> = {
  sin_cambio: "transparent",
  modificado: "#fff3cd", // Amarillo suave
  agregado: "#d4edda",   // Verde suave
  eliminado: "#f8d7da",   // Rojo suave
};

const ESTADO_LABELS: Record<EstadoComparacion, string> = {
  sin_cambio: "",
  modificado: "Modifica carga horaria / cursado",
  agregado: "Agrega materia",
  eliminado: "Elimina materia",
};

interface GroupedAnioComparacion {
  anio: number | string;
  rows: FilaComparacion[];
  totalHorasPci: { c1: number; c2: number };
}

// Muestra el cursado vigente; si el PCI lo cambió respecto de la base, "base → pci"
const renderCursado = (fila: FilaComparacion) => {
  if (fila.horasPci === undefined) return labelCursado(fila.cuatrimestreBase); // eliminado
  if (fila.horasBase === undefined) return labelCursado(fila.cuatrimestrePci); // agregado
  return fila.cuatrimestreBase === fila.cuatrimestrePci
    ? labelCursado(fila.cuatrimestrePci)
    : `${labelCursado(fila.cuatrimestreBase)} → ${labelCursado(fila.cuatrimestrePci)}`;
};

export const EspaciosComparador = ({
  espaciosBase,
  espaciosPci,
  maxHeight = "400px",
}: Props) => {
  const { filasComparacion, totalBase, totalPci } = useMemo(() => {
    const filas: FilaComparacion[] = [];

    // Set de IDs base que ya fueron matcheados para detectar los "agregados"
    const baseIdsMatched = new Set<string | number>();

    // Recorrer los espacios base y buscar su correspondiente PCI
    for (const base of espaciosBase) {
      const pciMatch = espaciosPci.find(
        (pci) => String(pci.espacioCurricularId) === String(base.id)
      );

      if (pciMatch) {
        baseIdsMatched.add(base.id);

        const nombreCambio =
          (base.nombre ?? "").trim().toLowerCase() !==
          (pciMatch.nombre ?? "").trim().toLowerCase();

        if (nombreCambio) {
          // Si el nombre cambió, se trata como una materia nueva (agregada)
          // y el espacio base original se considera eliminado.
          filas.push({
            nombre: base.nombre,
            anio: base.anio ?? 0,
            horasBase: base.horasSemanales,
            horasPci: undefined,
            cuatrimestreBase: base.cuatrimestre ?? null,
            area: base.area,
            tipoEspacio: base.tipoEspacio,
            estado: "eliminado",
          });
          filas.push({
            nombre: pciMatch.nombre,
            anio: pciMatch.anio ?? base.anio ?? 0,
            horasBase: undefined,
            horasPci: pciMatch.horasSemanales,
            cuatrimestrePci: pciMatch.cuatrimestre ?? null,
            area: pciMatch.area || base.area,
            tipoEspacio: pciMatch.tipoEspacio || base.tipoEspacio,
            estado: "agregado",
          });
        } else {
          // El nombre es el mismo, comparar carga horaria y cursado
          const horasBaseNum = Number(base.horasSemanales) || 0;
          const horasPciNum = Number(pciMatch.horasSemanales) || 0;
          const horasCambiaron = horasBaseNum !== horasPciNum;
          const cursadoCambio =
            (base.cuatrimestre ?? null) !== (pciMatch.cuatrimestre ?? null);

          filas.push({
            nombre: pciMatch.nombre || base.nombre,
            anio: pciMatch.anio ?? base.anio ?? 0,
            horasBase: base.horasSemanales,
            horasPci: pciMatch.horasSemanales,
            cuatrimestreBase: base.cuatrimestre ?? null,
            cuatrimestrePci: pciMatch.cuatrimestre ?? null,
            area: pciMatch.area || base.area,
            tipoEspacio: pciMatch.tipoEspacio || base.tipoEspacio,
            estado: horasCambiaron || cursadoCambio ? "modificado" : "sin_cambio",
          });
        }
      } else {
        // El espacio base fue eliminado en el PCI
        filas.push({
          nombre: base.nombre,
          anio: base.anio ?? 0,
          horasBase: base.horasSemanales,
          horasPci: undefined,
          cuatrimestreBase: base.cuatrimestre ?? null,
          area: base.area,
          tipoEspacio: base.tipoEspacio,
          estado: "eliminado",
        });
      }
    }

    // Espacios PCI sin correspondencia base → agregados
    for (const pci of espaciosPci) {
      if (!pci.espacioCurricularId) {
        // Sin vínculo a base → es nuevo
        filas.push({
          nombre: pci.nombre,
          anio: pci.anio ?? 0,
          horasBase: undefined,
          horasPci: pci.horasSemanales,
          cuatrimestrePci: pci.cuatrimestre ?? null,
          area: pci.area,
          tipoEspacio: pci.tipoEspacio,
          estado: "agregado",
        });
      } else {
        // Tiene vínculo pero no se encontró en base (doble check)
        const yaMatcheado = espaciosBase.some(
          (b) => String(b.id) === String(pci.espacioCurricularId)
        );
        if (!yaMatcheado) {
          filas.push({
            nombre: pci.nombre,
            anio: pci.anio ?? 0,
            horasBase: undefined,
            horasPci: pci.horasSemanales,
            cuatrimestrePci: pci.cuatrimestre ?? null,
            area: pci.area,
            tipoEspacio: pci.tipoEspacio,
            estado: "agregado",
          });
        }
      }
    }

    // Ordenar por año
    filas.sort((a, b) => (Number(a.anio) || 0) - (Number(b.anio) || 0));

    const totalBase = horasPorCuatrimestre(
      filas.map((f) => ({ horasSemanales: f.horasBase, cuatrimestre: f.cuatrimestreBase }))
    );
    const totalPci = horasPorCuatrimestre(
      filas.map((f) => ({ horasSemanales: f.horasPci, cuatrimestre: f.cuatrimestrePci }))
    );

    return { filasComparacion: filas, totalBase, totalPci };
  }, [espaciosBase, espaciosPci]);

  // Agrupar por año para rowSpan
  const groupedByAnio: GroupedAnioComparacion[] = useMemo(() => {
    const groups = new Map<string | number, FilaComparacion[]>();

    for (const fila of filasComparacion) {
      const anio = fila.anio ?? 0;
      if (!groups.has(anio)) {
        groups.set(anio, []);
      }
      groups.get(anio)!.push(fila);
    }

    return Array.from(groups.entries()).map(([anio, rows]) => ({
      anio,
      rows,
      totalHorasPci: horasPorCuatrimestre(
        rows.map((r) => ({ horasSemanales: r.horasPci, cuatrimestre: r.cuatrimestrePci }))
      ),
    }));
  }, [filasComparacion]);

  if (filasComparacion.length === 0) {
    return (
      <p className="text-muted small text-center mb-0">
        No hay datos para comparar.
      </p>
    );
  }

  return (
    <div>
      <div style={{ maxHeight, overflowY: "auto" }}>
        <Table bordered hover size="sm" className="mb-0">
          <thead
            className="table-light"
            style={{ position: "sticky", top: 0 }}
          >
            <tr>
              <th className="text-center">Año</th>
              <th>Nombre</th>
              <th className="text-center">Hs Base</th>
              <th className="text-center">Hs PCI</th>
              <th className="text-center">Cursado</th>
              <th>Área</th>
              <th>Tipo Espacio</th>
              <th className="text-center">Total 1C (PCI)</th>
              <th className="text-center">Total 2C (PCI)</th>
            </tr>
          </thead>
          <tbody>
            {groupedByAnio.map((group) =>
              group.rows.map((fila, rowIdx) => {
                const rowBg =
                  fila.estado !== "sin_cambio"
                    ? ESTADO_COLORS[fila.estado]
                    : undefined;
                return (
                  <tr key={`${group.anio}-${rowIdx}`}>
                    {rowIdx === 0 && (
                      <td
                        rowSpan={group.rows.length}
                        className="text-center align-middle fw-semibold"
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        {group.anio}
                      </td>
                    )}
                    <td style={rowBg ? { backgroundColor: rowBg } : undefined}>
                      {fila.nombre}
                    </td>
                    <td
                      className="text-center"
                      style={rowBg ? { backgroundColor: rowBg } : undefined}
                    >
                      {fila.horasBase ?? "-"}
                    </td>
                    <td
                      className="text-center"
                      style={{
                        ...(rowBg ? { backgroundColor: rowBg } : {}),
                        fontWeight:
                          fila.estado === "modificado" ? "bold" : "normal",
                      }}
                    >
                      {fila.horasPci ?? "-"}
                    </td>
                    <td
                      className="text-center"
                      style={rowBg ? { backgroundColor: rowBg } : undefined}
                    >
                      {renderCursado(fila)}
                    </td>
                    <td style={rowBg ? { backgroundColor: rowBg } : undefined}>
                      {fila.area ?? "-"}
                    </td>
                    <td style={rowBg ? { backgroundColor: rowBg } : undefined}>
                      {fila.tipoEspacio ?? "-"}
                    </td>
                    {rowIdx === 0 && (
                      <>
                        <td
                          rowSpan={group.rows.length}
                          className="text-center align-middle fw-bold"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          {group.totalHorasPci.c1}
                        </td>
                        <td
                          rowSpan={group.rows.length}
                          className="text-center align-middle fw-bold"
                          style={{ backgroundColor: "#f8f9fa" }}
                        >
                          {group.totalHorasPci.c2}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot className="table-light">
            {/* Totales de horas semanales por cuatrimestre, debajo de Total 1C / Total 2C */}
            <tr>
              <td></td>
              <td className="text-end fw-bold">Plan base</td>
              <td colSpan={5}></td>
              <td className="text-center fw-bold">{totalBase.c1} hs</td>
              <td className="text-center fw-bold">{totalBase.c2} hs</td>
            </tr>
            <tr>
              <td></td>
              <td className="text-end fw-bold">PCI</td>
              <td colSpan={5}></td>
              <td className="text-center fw-bold">{totalPci.c1} hs</td>
              <td className="text-center fw-bold">{totalPci.c2} hs</td>
            </tr>
          </tfoot>
        </Table>
      </div>

      {/* Leyenda */}
      <div className="d-flex gap-4 mt-2 flex-wrap small">
        {(["modificado", "agregado", "eliminado"] as EstadoComparacion[]).map(
          (estado) => (
            <div key={estado} className="d-flex align-items-center gap-1">
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: ESTADO_COLORS[estado],
                  border: "1px solid #ccc",
                  borderRadius: "2px",
                }}
              ></div>
              <span>{ESTADO_LABELS[estado]}</span>
            </div>
          )
        )}
      </div>
    </div>
  );
};
